import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  CircularProgress
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { authDarkFieldSx, authPrimaryButtonSx, authLinkButtonSx } from './authStyles';

const SignUpForm = ({ onToggleMode, onVerificationNeeded }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signUp } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);

    try {
      await signUp(email, password, {
        name: name
      });
      onVerificationNeeded(email);
    } catch (err) {
      setError(err.message || 'Failed to sign up');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" component="h1" fontWeight={700} gutterBottom sx={{ color: 'white' }}>
        Create Account
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, color: 'rgba(255,255,255,0.7)' }}>
        Join thousands of professionals building better resumes
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 1 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label="Full Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          margin="normal"
          required
          autoComplete="name"
          sx={authDarkFieldSx}
        />
        <TextField
          fullWidth
          label="Email Address"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          margin="normal"
          required
          autoComplete="email"
          sx={authDarkFieldSx}
        />
        <TextField
          fullWidth
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          margin="normal"
          required
          autoComplete="new-password"
          helperText="Minimum 8 characters"
          sx={authDarkFieldSx}
        />
        <TextField
          fullWidth
          label="Confirm Password"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          margin="normal"
          required
          autoComplete="new-password"
          sx={authDarkFieldSx}
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={loading}
          sx={{ ...authPrimaryButtonSx, mt: 2 }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Create Account'}
        </Button>
      </form>

      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
          Already have an account?{' '}
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={onToggleMode}
            sx={authLinkButtonSx}
          >
            Sign In
          </Link>
        </Typography>
      </Box>

      <Typography
        variant="caption"
        sx={{ display: 'block', textAlign: 'center', mt: 2, lineHeight: 1.5, color: 'rgba(255,255,255,0.5)' }}
      >
        By signing up, you agree to our Terms of Service and Privacy Policy
      </Typography>
    </Box>
  );
};

export default SignUpForm;

